<template>
    <v-card width="100%" class="rounded-xl" v-resize="calculateTableHeight">
        <v-card-title class="primary--text text--darken-2 px-5 pb-0 d-flex card-title-footer-color">
            <v-row class="pa-0 ma-0">
                <!-- Campo Buscador -->
                <v-col cols="3" class="pa-0 ma-0">
                    <v-text-field
                        v-if="searchFilters.length > 0"
                        v-model="search"
                        placeholder="Buscar"
                        color="primary"
                        class="rounded-lg ma-0 pb-3 pt-0 text-field-size input-search"
                        @input="getItems"
                        hide-details="auto"
                        height="36px"
                        prepend-inner-icon="mdi-magnify"
                        clearable
                    ></v-text-field>
                </v-col>
                <v-col cols="9" class="pa-0 ma-0">
                    <div v-if="url === 'async_command_bus/'" align="right">
                        <JobProgress v-model="asyncJobState" v-on:done="jobDone" :job_id="asyncJobId">
                            <template v-slot:progress>
                                <v-progress-circular
                                    v-if="asyncJobState==='running'"
                                    indeterminate           
                                    color="primary"
                                    class="progress_circle"
                                />
                            </template>
                            <template v-slot:done>
                                <v-tooltip top>
                                    <template v-slot:activator="{ on, attrs }">
                                        <v-btn @click="getItems()" color="primary"
                                            icon v-bind="attrs" v-on="on">
                                            <v-icon>mdi-refresh</v-icon>
                                        </v-btn>
                                    </template>
                                    <span>Actualizar</span>
                                </v-tooltip>
                            </template>
                        </JobProgress>
                    </div>
                    <slot v-else name="search_right"/>
                </v-col>
            </v-row>
        </v-card-title>
        <v-divider />
        <v-card-text class="px-0 pt-0 card-title-footer-color">
            <span v-if="showSelect && !hideHeaderCheckbox">
                <v-checkbox
                    v-if="showSelectAll()"
                    class="ma-0 pt-5 pl-4"
                    v-model="selectAllItems"
                    :disabled="loadingItems"
                    @change="changeSelectAllItems"
                >
                    <template v-slot:label>
                        <span class="text-field-size">Aplicar en los {{totalItems}} elementos</span>
                    </template>
                </v-checkbox>
            </span>
            <v-data-table
                v-model="selectedItems"
                :items="items"
                :footer-items-per-page-text="footerItemsPerPageText"
                :footer-items-per-page-options="footerItemsPerPageOptions"
                :headers="headers"
                :show-select="showSelect"
                :loading="loadingItems"
                :server-items-length="totalItems"
                :page.sync="currentPage"
                :footer-props="footerProps"
                :action-btn-list="actionBtnList"
                :no-data-text="textNoResults"
                :loading-text="textLoading"
                :class="className"
                @click:row="clickRow"
                @update:items-per-page="changeLimit"
                @update:page="changeOffset"
                @input="checkItemSelected"
                @update:sort-by="sortTable"
                @update:sort-desc="sortTableDesc"
                @toggle-select-all=""
                :height="tableHeight"
                fixed-header
                :item-key="itemKey"
                :mobile="mobile"
            >
                <!-- Columnas Personalizables -->
                <template v-for="header in headers" v-slot:[`item.${header.value}`]="{ item }">
                    <span :class="isDisabledRow(item) ? 'font-color-disabled': 'font-color'">
                        <slot :name="header.value" v-bind="item">{{ item[header.value] }}</slot>
                    </span>
                </template>
                <template v-if="hideHeaderCheckbox" #header.data-table-select></template>
                 <!-- Header Personalizables -->
                <template v-for="header in headers" v-slot:[`header.${header.value}`]="{ header }">
                    <v-checkbox
                        v-if="header.checkbox"
                        class="ma-0 pa-0"
                        @change="changeColSelected(header)"
                        :value="header.selected"
                        hide-details
                    >
                        <template v-slot:label>
                            <span>{{ header.text }}</span>
                        </template>
                    </v-checkbox>
                    <span v-else tabindex="0">
                        {{ header.text }}
                    </span>
                </template>
                <!-- Columna "checkboxes" con los checks de cada fila-->
                <template v-slot:item.data-table-select="{ item, isSelected, select }">
                    <v-checkbox
                        class="ma-0 pa-0"
                        :value="isSelected" 
                        :disabled="isDisabledCheckRow(item)"
                        @change="select($event)" 
                        hide-details 
                    />
                </template>
                <!-- Columna "actions" con los botones de acciones-->
                <template v-if="showBtnList && actionBtnList.length > 0" v-slot:item.actions="{ item }">
                    <v-tooltip top v-for="actionBtn in actionBtnList">
                        <template v-slot:activator="{ on, attrs }">
                            <v-btn @click="clickColActionBtn(actionBtn.action, item)" color="primary" v-if="showItemActionBtn(actionBtn, item)" :disabled="isDisabledRow(item)" icon v-bind="attrs" v-on="on">
                                <v-icon>{{actionBtn.icon}}</v-icon>
                            </v-btn>
                        </template>
                        <span>{{actionBtn.name}}</span>
                    </v-tooltip>
                </template>
                <!--Footer: Paginación-->
                <template v-slot:[`footer.page-text`]="items">
                    {{ items.pageStart }} - {{ items.pageStop }} de {{ items.itemsLength }}
                </template>
            </v-data-table>
        </v-card-text>
    </v-card>
</template>

<script>
import { client } from "@/api/client"
import AppTextField from '@/components/layout/AppTextField.vue';
import JobProgress from '../components/JobProgress.vue';
import { jobs } from '../api/jobs.js';

export default {
    name: 'DataTableWithApi',
    components: {
        AppTextField,
        JobProgress,
    },
    props: {
        headers: {
            type: Array,
            default: [],
        },
        url: {
            type: String,
            required: true
        },
        footerItemsPerPageText: {
            type: String,
            default: 'Registros por página:'
        },
        footerItemsPerPageOptions: {
            type: Array,
            default: () => [5, 10, 100, 1000],
        },
        searchFilters: {
            type: Array,
            default: [],
        },
        showSelect: {
            type: Boolean,
            default: false,
        },
        showBtnList: {
            type: Boolean,
            default: true,
        },
        actionBtnList: {
            type: Array,
            default() {
                 return [];
            }
        },
        textNoResults: {
            type: String,
            default: 'No se han encontrado registros'
        },
        textLoading: {
            type: String,
            default: 'Cargando...'
        },
        itemKey: {
            type: String,
            default: 'id',
        },
        hideHeaderCheckbox: {
            type: Boolean,
            default: false
        },
        className: {
            type: String,
            default: ''
        },
        mobile: {
            type: Boolean,
            default: false
        },
        api_params: {
            type: Object,
            default: undefined,
        },
        disabledRowField: {
            type: String,
            default: ''
        },
        disabledCheckRowField: {
            type: String,
            default: ''
        }
    },
    data() {
        return {
            selectedItems: [],
            items: [],
            limit: 10,
            offset: 0,
            currentPage: 1,
            search: '',
            loadingItems: false,
            totalItems: 0,
            footerProps: {
                'items-per-page-text': this.footerItemsPerPageText,
                'items-per-page-options': this.footerItemsPerPageOptions,
            },
            selectAllItems: false,
            tableHeight: '59vh',
            initNumItemsPerPage: 0,
            sortBy: "",
            getItemsAbortController: null,
            asyncJobId:0,
            asyncJobState:'done'
        }
    },
    methods: {
        resetPagination() {
            this.offset = 0;
            this.currentPage = 1;
        },
        getParams(signal) {
            let params = {};
            if (this.search != '') {
                this.resetPagination();
                for (let i in this.searchFilters) {
                    params[this.searchFilters[i]] = this.search;
                }
            }
            params['limit'] = this.limit;
            params['offset'] = this.offset;
            if (this.sortBy) {
                params['ordering'] = this.sortBy;
            }
            for (let key in this.api_params) {
                params[key] = this.api_params[key];
            }
            if (params.hasOwnProperty('command') && params.command.hasOwnProperty('cmd_name') && params.command.cmd_name == 'get.access.registers') {
                params.command.limit = this.limit
                params.command.offset = this.offset
            }
            return {params: params, signal: signal};
        },
        getItems() {
            return new Promise((resolve, reject) => {
                if (this.getItemsAbortController) {
                    this.getItemsAbortController.abort(); //Cancelamos la llamada anterior si aún no ha terminado.
                }
                this.getItemsAbortController = new AbortController();
                let params = this.getParams(this.getItemsAbortController.signal);
                this.loadingItems = true;
                const fetchData = (requestMethod) => {
                    return requestMethod(this.url, params).then(response => {
                            this.items = [];
                            this.$nextTick(() => { // Para asegurar que Vue detecte los cambios en `items`.
                                if (this.url == 'async_command_bus/') {
                                    this.asyncJobState = 'running'
                                    this.asyncJobId = response.data.job_id
                                    
                                } else {
                                    this.items = this.url === 'async_command_bus/' ? response.data.output.results : response.data.results;
                                    this.totalItems = this.url === 'async_command_bus/' ? response.data.output.count : response.data.count;
                                    this.selectedItems = [];
                                    this.selectAllItems = false;
                                    this.calculateTableHeight();
                                    this.loadingItems = false;
                                }
                            });
                            resolve();
                    }).catch(error => {
                        if (error.name === 'CanceledError') {
                            console.log('Petición cancelada');
                            resolve();
                        } else {
                            reject(error);
                        }
                    });
                };

                if (this.url.includes('async_command_bus/', 'sync_command_bus/')) {
                    params = params.params.command
                    fetchData(client.post);
                } else {
                    fetchData(client.get);
                }
            });
        },
        async jobDone(){
            const response = await jobs.getJob({ id: this.asyncJobId });
            this.asyncJobState = response.data.state
            this.items = response.data.output_info['results']
            this.totalItems = response.data.output_info['count']
            this.selectedItems = [];
            this.selectAllItems = false;
            this.calculateTableHeight();
            if (this.asyncJobState == 'done') {
                this.loadingItems = false;
            }
        },
        changeOffset(value) {
            this.offset = this.limit*(value-1);
            this.getItems();
        },
        changeLimit(value) {
            if (value == -1) { //Option "All"
                value = 100000;
            }
            this.limit = value;
            this.getItems();
        },
        clickRow(params) {
            this.$emit('open-update-row', params);
        },
        showSelectAll() {
            let length_items = this.items.length;
            let length_selected = this.selectedItems.length;
            if (length_items == length_selected && length_items != 0) {
                return true;
            }
            return false;
        },
        checkItemSelected() {
            this.selectAllItems = false;
            this.$emit('selected-items-changed', this.selectedItems, this.selectAllItems, {});
        },

        changeSelectAllItems() {
            let search_params = {};
            let selectedItemsAux = this.selectedItems;
            if (this.selectAllItems) {
                for (let i in this.searchFilters) {
                    search_params[this.searchFilters[i]] = this.search;
                }
                selectedItemsAux = [];
            }
            this.$emit('selected-items-changed', selectedItemsAux, this.selectAllItems, search_params);
        },
        changeColSelected(item) {
            this.$emit('selected-cols-changed', item);
        },
        clickColActionBtn(action, item) {
            this.$emit('click-col-action-btn', action, item);
        },
        calculateTableHeight() {
            this.initNumItemsPerPage = Math.min(this.limit, 10);
            if (this.totalItems == 0) {
                this.tableHeight = '20vh';
            }
            else {
                const rowHeight = document.querySelector('.text-start') && document.querySelector('.text-start').offsetHeight || 48;
                this.tableHeight = rowHeight * (Math.min(this.totalItems, this.initNumItemsPerPage) + 1).toString() + 'px';
            }
        },
        sortTable(params) {
            this.sortBy = "";
            if (params && params[0]) {
                this.sortBy = params[0];
                this.getItems();
            }
        },
        sortTableDesc(params) {
            if (params && params[0] && this.sortBy) {
                this.sortBy = '-' + this.sortBy;
                this.getItems();
            }
        },
        isDisabledRow(item) {
            return this.disabledRowField != '' && item.hasOwnProperty(this.disabledRowField) && item[this.disabledRowField];
        },
        isDisabledCheckRow(item) {
            return this.disabledCheckRowField != '' && item.hasOwnProperty(this.disabledCheckRowField) && item[this.disabledCheckRowField];
        },
        showItemActionBtn(actionBtn, item) {
            let item_show_btn = actionBtn.hasOwnProperty('show_field') && actionBtn['show_field'];
            if (item_show_btn && item.hasOwnProperty(item_show_btn)) {
                return item[item_show_btn];
            }
            return true;
        }
    },
    mounted() {
        this.getItems();
    },
}
</script>

<style scoped>

    .card-title-footer-color {
        background: #FAFAFF;
    }
    ::v-deep .v-data-footer {
        background-color: #FAFAFF;
        margin-right: 0px !important;
        padding-right:  17px;
    }
    ::v-deep .v-data-table-header > tr > th{
        background-color: var(--v-primaryLight-base) !important;
    }
    ::v-deep .v-data-table-header > tr > th > span,
    ::v-deep .v-data-table-header > tr > th >  div > div > div > label > span{
        color: var(--v-primary-base);
        font-size: 0.875rem;
    }
    .font-color {
        color: #56565B;
    }
    .font-color-disabled {
        color: #56565B;
        opacity: 0.5;
    }

    ::v-deep .v-data-table__wrapper > table > tbody > tr > td {
        border-bottom: none !important;
    }

    ::v-deep tbody tr:nth-of-type(even) {
        /* 'deep-orange lighten-5' basides on material design color */
        background-color: rgba(245, 245, 245, 0.47);
    }

    ::v-deep .v-input__prepend-inner > div > i {
        color: var(--v-primary-base);
        top: 2px;
        padding-left: 5px;
    }

    .text-field-size {
        font-weight: normal;
        font-size: 0.875rem;
    }
    ::v-deep .input-search .v-input__slot::before {
         border-style: none !important;
    }

    ::v-deep .input-search .v-text-field__slot,
    ::v-deep .input-search .v-input__prepend-inner,
    ::v-deep .input-search .v-input__slot {
        background-color: #F2F1FF !important;
    }

</style>